// LOCATION: admin-portal/src/pages/Subscribers.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase'; 
import { 
  collection, onSnapshot, query, where, getDocs, 
  writeBatch, doc, Timestamp, serverTimestamp, limit, orderBy, startAt, endAt 
} from 'firebase/firestore';
import * as XLSX from 'xlsx'; 
import './UserManagement.css'; 

const Subscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('All'); // 'All', 'Paid', 'Offer', 'Business'
  
  // Offer Activation States
  const [showModal, setShowModal] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [foundUser, setFoundUser] = useState(null);
  const [activating, setActivating] = useState(false);

  // 1. Fetch Suggestions for Auto-complete
  useEffect(() => {
    const fetchSuggestions = async () => {
      const input = searchEmail.trim().toLowerCase();
      if (input.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const q = query(
          collection(db, "profiles"),
          orderBy("email"),
          startAt(input),
          endAt(input + "\uf8ff"),
          limit(5)
        );
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSuggestions(list);
      } catch (e) {
        console.error("Suggestion error:", e);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchEmail]);

  // 2. Real-time Subscription Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'profiles'), (snapshot) => {
      const subs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(profile => profile.plan && profile.plan !== 'FREE'); 

      setSubscribers(subs);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 3. Filter Logic
  const filteredList = useMemo(() => {
    if (filterType === 'Paid') return subscribers.filter(s => !s.grantedOffer && s.businessType !== 'Business');
    if (filterType === 'Offer') return subscribers.filter(s => s.grantedOffer);
    if (filterType === 'Business') return subscribers.filter(s => s.businessType === 'Business');
    return subscribers;
  }, [subscribers, filterType]);

  // 4. Revenue Calculation (Paid + Business)
  const totalRevenue = useMemo(() => {
    // Only exclude 'grantedOffer'. Include standard Paid and Business users.
    const revenueCount = filteredList.filter(s => !s.grantedOffer).length;
    return revenueCount * 1500;
  }, [filteredList]);

  const selectUser = (user) => {
    setFoundUser(user);
    setSearchEmail(user.email);
    setSuggestions([]);
  };

  const activateOffer = async () => {
    if (!foundUser) return;
    setActivating(true);
    
    try {
      const batch = writeBatch(db);
      const expiry = new Date();
      expiry.setMonth(expiry.getMonth() + 1);

      batch.update(doc(db, 'profiles', foundUser.id), {
        plan: "PLUS",
        planExpiry: Timestamp.fromDate(expiry),
        grantedOffer: true,
        updatedAt: serverTimestamp() 
      });

      const invRef = doc(collection(db, `users/${foundUser.id}/invoices`));
      batch.set(invRef, {
        invoiceNumber: `OFFER-${Math.floor(1000 + Math.random() * 9000)}`,
        amount: 0,
        status: "Paid",
        startDate: serverTimestamp(),
        endDate: Timestamp.fromDate(expiry),
        createdAt: serverTimestamp(),
        isOffer: true
      });

      await batch.commit();
      alert("Offer Activated Successfully!");
      setShowModal(false);
      setFoundUser(null);
      setSearchEmail('');
    } catch (e) { 
      console.error("Activation failed:", e);
      alert("Error: " + e.message); 
    } finally {
      setActivating(false); 
    }
  };

  const exportToExcel = () => {
    const data = filteredList.map(s => ({
      "Name": s.name || 'Unknown',
      "Email": s.email,
      "Plan": s.plan,
      "Type": s.businessType === 'Business' ? "Business" : (s.grantedOffer ? "Offer" : "Paid"),
      "Expiry": s.planExpiry ? (typeof s.planExpiry === 'string' ? s.planExpiry : s.planExpiry.toDate().toLocaleDateString()) : 'N/A'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Subscribers");
    XLSX.writeFile(wb, `Subscribers_${filterType}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);

  if (loading) return <div className="container">Loading Subscribers...</div>;

  return (
    <div className="container">
      <div className="header">
        <h1>💳 Subscribers</h1>
        <div style={{display:'flex', gap:'10px'}}>
            <button onClick={exportToExcel} className="export-btn">📊 Export Excel</button>
            <button onClick={() => setShowModal(true)} className="fab-btn">+</button>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="stat-card">
            {/* ✅ Logic: Revenue now reflects the currently filtered view */}
            <div className="stat-title">{filterType} Revenue (Est.)</div>
            <div className="total-number" style={{color:'#10b981'}}>{formatNaira(totalRevenue)}</div>
            <div className="stat-label">Excluding manual offers</div>
        </div>
        <div className="stat-card">
            {/* ✅ Logic: Total reflects the currently filtered count */}
            <div className="stat-title">Total {filterType !== 'All' ? filterType : ''} Subscribers</div>
            <div className="total-number">{filteredList.length}</div>
            <div className="stat-label">
                {filteredList.filter(s => s.grantedOffer).length} active offers in view
            </div>
        </div>
      </div>

      <div className="filters">
        <select className="type-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="All">All Types</option>
          <option value="Paid">Paid Only</option>
          <option value="Offer">Offers Only</option>
          {/* ✅ New Filter: Business */}
          <option value="Business">Business Only</option>
        </select>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>User Name</th>
              <th>Email</th>
              <th>Plan / Status</th>
              <th>Expiry Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map(sub => (
              <tr key={sub.id}>
                <td>
                    <div style={{fontWeight:'bold'}}>{sub.name || 'Unknown'}</div>
                    <div style={{display:'flex', gap: '5px', marginTop: '4px'}}>
                        {sub.grantedOffer && <span className="offer-tag">OFFER GRANTED</span>}
                        {/* ✅ Added Business Badge */}
                        {sub.businessType === 'Business' && <span className="business-tag">BUSINESS</span>}
                    </div>
                </td>
                <td>{sub.email}</td>
                <td><span className="badge badge-purple">{sub.plan}</span></td>
                <td>
                    {sub.planExpiry ? (typeof sub.planExpiry === 'string' ? sub.planExpiry : sub.planExpiry.toDate().toLocaleDateString()) : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Manual Activation Modal (unchanged) */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Activate Manual Offer</h3>
            <div className="search-container">
              <input 
                type="email" 
                placeholder="Start typing email..." 
                value={searchEmail} 
                autoComplete="off"
                onChange={e => {
                    setSearchEmail(e.target.value);
                    if (foundUser) setFoundUser(null);
                }} 
              />
              {suggestions.length > 0 && (
                <div className="autocomplete-dropdown">
                  {suggestions.map(s => (
                    <div key={s.id} className="suggestion-item" onClick={() => selectUser(s)}>
                      <strong>{s.name}</strong> <br/>
                      <small>{s.email}</small>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {foundUser && (
              <div className="user-details-card">
                <p><strong>Selected:</strong> {foundUser.name}</p>
                <button 
                  className="activate-btn" 
                  onClick={activateOffer} 
                  disabled={activating}
                >
                  {activating ? "Activating..." : "Activate Offer"}
                </button>
              </div>
            )}
            <button className="close-btn" onClick={() => {setShowModal(false); setSuggestions([]);}}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscribers;